import { defineStore } from 'pinia';
import { ref } from 'vue';
import { supervisorHas } from '@neon-one/auctions-types';
import type {
  SupervisorAccess,
  SupervisorRequirement,
  SupervisorOrganization,
  SupervisorListItem,
  SsoOrgDetail,
  SsoOrgUser,
  SsoInstance,
  SsoUserLookupResponse,
  CreateOrganizationRequest,
  FeatureFlag,
  FeatureFlagUpdate,
  FeatureFlagScheduleInput,
  StuckCharge,
  ResolveStuckChargeRequest,
  StuckRefund,
  ResolveStuckRefundRequest,
  StrandedVerificationHold,
  RetryHoldReleaseResult,
} from '@neon-one/auctions-types';
import { supervisorApi } from './api';

export const useSupervisorStore = defineStore('supervisor', () => {
  const orgs = ref<SupervisorOrganization[]>([]);
  const supervisors = ref<SupervisorListItem[]>([]);
  const ssoOrg = ref<SsoOrgDetail | null>(null);
  const ssoOrgUsers = ref<SsoOrgUser[]>([]);
  const ssoOrgInstances = ref<SsoInstance[]>([]);
  const auctionsInstances = ref<SsoInstance[]>([]);
  const bidderUrlTemplate = ref<string | null>(null);
  const loading = ref(false);

  // The signed-in supervisor's access. Re-read on every supervisor page mount
  // and route guard rather than cached: it lives on the supervisors row, not
  // the session token, and a super admin can change it at any time. Until the
  // first read lands, can() answers false, so gated controls stay hidden.
  const access = ref<SupervisorAccess | null>(null);
  let accessRequest: Promise<SupervisorAccess> | null = null;

  async function loadAccess(): Promise<SupervisorAccess> {
    // Concurrent callers (the shell and a route guard on the same navigation)
    // share one request.
    accessRequest ??= supervisorApi
      .getAccess()
      .then((resp) => (access.value = resp.data))
      .finally(() => {
        accessRequest = null;
      });
    return accessRequest;
  }

  function can(requirement: SupervisorRequirement): boolean {
    return access.value !== null && supervisorHas(access.value, requirement);
  }

  async function loadConfig(): Promise<void> {
    const resp = await supervisorApi.getConfig();
    bidderUrlTemplate.value = resp.data.bidderUrlTemplate;
  }

  async function loadOrgs(): Promise<void> {
    loading.value = true;
    try {
      const resp = await supervisorApi.listOrgs();
      orgs.value = resp.data;
    } finally {
      loading.value = false;
    }
  }

  async function provisionOrg(data: CreateOrganizationRequest): Promise<SupervisorOrganization> {
    const resp = await supervisorApi.createOrg(data);
    orgs.value = [resp.data, ...orgs.value];
    return resp.data;
  }

  async function loadSupervisors(): Promise<void> {
    const resp = await supervisorApi.listSupervisors();
    supervisors.value = resp.data;
  }

  async function addSupervisor(
    email: string,
    displayName: string | null,
    grant: SupervisorAccess,
  ): Promise<void> {
    await supervisorApi.addSupervisor({ email, displayName, ...grant });
    await loadSupervisors();
  }

  async function updateSupervisorAccess(email: string, grant: SupervisorAccess): Promise<void> {
    const resp = await supervisorApi.updateSupervisorAccess(email, grant);
    supervisors.value = supervisors.value.map((s) => (s.email === email ? resp.data : s));
  }

  async function removeSupervisor(email: string): Promise<void> {
    await supervisorApi.removeSupervisor(email);
    await loadSupervisors();
  }

  async function fetchSsoOrg(ssoOrgId: number): Promise<void> {
    const resp = await supervisorApi.getSsoOrg(ssoOrgId);
    ssoOrg.value = resp.data;
  }

  async function searchSsoOrg(params: { salesforceId?: string; ein?: string }): Promise<void> {
    const resp = await supervisorApi.searchSsoOrg(params);
    ssoOrg.value = resp.data;
  }

  async function fetchSsoOrgUsers(ssoOrgId: number): Promise<void> {
    const resp = await supervisorApi.getSsoOrgUsers(ssoOrgId);
    ssoOrgUsers.value = resp.data;
  }

  async function lookupOrgUser(ssoOrgId: number, email: string): Promise<SsoUserLookupResponse> {
    const resp = await supervisorApi.lookupOrgUser(ssoOrgId, email);
    ssoOrgInstances.value = resp.data.crmInstances;
    auctionsInstances.value = resp.data.auctionsInstances;
    return resp.data;
  }

  async function fetchSsoOrgInstances(ssoOrgId: number): Promise<void> {
    const resp = await supervisorApi.getSsoOrgInstances(ssoOrgId);
    ssoOrgInstances.value = resp.data;
  }

  function resetSsoState(): void {
    ssoOrg.value = null;
    ssoOrgUsers.value = [];
    ssoOrgInstances.value = [];
    auctionsInstances.value = [];
  }

  const featureFlags = ref<FeatureFlag[]>([]);
  const flagsLoading = ref(false);

  async function loadFeatureFlags(): Promise<void> {
    flagsLoading.value = true;
    try {
      const resp = await supervisorApi.listFeatureFlags();
      featureFlags.value = resp.data;
    } finally {
      flagsLoading.value = false;
    }
  }

  async function updateFeatureFlag(key: string, data: FeatureFlagUpdate): Promise<void> {
    const resp = await supervisorApi.updateFeatureFlag(key, data);
    featureFlags.value = resp.data;
  }

  async function setFeatureFlagOverride(
    key: string,
    salesforceId: string,
    enabled: boolean,
  ): Promise<void> {
    const resp = await supervisorApi.setFeatureFlagOverride(key, salesforceId, enabled);
    featureFlags.value = resp.data;
  }

  async function removeFeatureFlagOverride(key: string, salesforceId: string): Promise<void> {
    const resp = await supervisorApi.removeFeatureFlagOverride(key, salesforceId);
    featureFlags.value = resp.data;
  }

  async function setFeatureFlagSchedule(key: string, body: FeatureFlagScheduleInput): Promise<void> {
    const resp = await supervisorApi.setFeatureFlagSchedule(key, body);
    featureFlags.value = resp.data;
  }

  async function clearFeatureFlagSchedule(key: string): Promise<void> {
    const resp = await supervisorApi.clearFeatureFlagSchedule(key);
    featureFlags.value = resp.data;
  }

  // --- stuck charges (cross-org invoices wedged in `processing`) ---
  const stuckCharges = ref<StuckCharge[]>([]);
  const stuckChargesLoading = ref(false);

  async function loadStuckCharges(): Promise<void> {
    stuckChargesLoading.value = true;
    try {
      const resp = await supervisorApi.listStuckCharges();
      stuckCharges.value = resp.data;
    } finally {
      stuckChargesLoading.value = false;
    }
  }

  // Resolve returns the updated invoice, but a resolved charge leaves the
  // stuck set entirely — reload rather than patch the row in place.
  async function resolveStuckCharge(
    invoiceUuid: string,
    data: ResolveStuckChargeRequest,
  ): Promise<void> {
    await supervisorApi.resolveStuckCharge(invoiceUuid, data);
    await loadStuckCharges();
  }

  // --- stuck refunds (money we told Neon Pay to send back and lost track of) ---
  // Its own collection for the same reason stranded holds are: a stuck charge
  // is money a bidder owes, a stuck refund is money already taken that we have
  // not proved went back.
  const stuckRefunds = ref<StuckRefund[]>([]);
  const stuckRefundsLoading = ref(false);

  async function loadStuckRefunds(): Promise<void> {
    stuckRefundsLoading.value = true;
    try {
      const resp = await supervisorApi.listStuckRefunds();
      stuckRefunds.value = resp.data;
    } finally {
      stuckRefundsLoading.value = false;
    }
  }

  // Reload rather than patch: either resolution takes the row out of the stuck
  // set entirely.
  async function resolveStuckRefund(
    paymentTransactionId: number,
    data: ResolveStuckRefundRequest,
  ): Promise<void> {
    await supervisorApi.resolveStuckRefund(paymentTransactionId, data);
    await loadStuckRefunds();
  }

  // --- stranded verification holds (authorizations we failed to release) ---
  // Its own collection, never merged into `stuckCharges`: the two lists share a
  // page but not a wire type, a vocabulary, or an action (spec §8.3).
  const strandedHolds = ref<StrandedVerificationHold[]>([]);
  const strandedHoldsLoading = ref(false);

  async function loadStrandedHolds(): Promise<void> {
    strandedHoldsLoading.value = true;
    try {
      const resp = await supervisorApi.listStrandedHolds();
      strandedHolds.value = resp.data;
    } finally {
      strandedHoldsLoading.value = false;
    }
  }

  // Reload rather than patch: a released hold leaves the list entirely, while a
  // still-pending one stays with a fresh attempt count and error. One refetch
  // covers both without the caller having to know which happened.
  async function retryHoldRelease(
    paymentMethodPublicId: string,
  ): Promise<RetryHoldReleaseResult> {
    const resp = await supervisorApi.retryHoldRelease(paymentMethodPublicId);
    await loadStrandedHolds();
    return resp.data;
  }

  return {
    orgs, supervisors, ssoOrg, ssoOrgUsers, ssoOrgInstances, auctionsInstances, bidderUrlTemplate, loading,
    featureFlags, flagsLoading,
    stuckCharges, stuckChargesLoading,
    loadStuckCharges, resolveStuckCharge,
    stuckRefunds, stuckRefundsLoading,
    loadStuckRefunds, resolveStuckRefund,
    strandedHolds, strandedHoldsLoading,
    loadStrandedHolds, retryHoldRelease,
    access, loadAccess, can,
    loadConfig, loadOrgs, provisionOrg, loadSupervisors, addSupervisor, updateSupervisorAccess, removeSupervisor,
    fetchSsoOrg, searchSsoOrg, fetchSsoOrgUsers, lookupOrgUser, fetchSsoOrgInstances, resetSsoState,
    loadFeatureFlags, updateFeatureFlag, setFeatureFlagOverride, removeFeatureFlagOverride,
    setFeatureFlagSchedule, clearFeatureFlagSchedule,
  };
});
